import { ArrowDown, ArrowUp, ArrowUpDown, Columns } from 'lucide-react'
import type { ReactNode } from 'react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { TypeSortDirection } from '@/libs/interfaces'
import { cn } from '@/libs/utils'

interface Column<T> {
  key: keyof T | string
  header: string
  render?: (item: T) => ReactNode
  className?: string
  sortable?: boolean
  searchable?: boolean
  alwaysVisible?: boolean
  hiddenByDefault?: boolean
}

type SortDirection = TypeSortDirection | null

interface DataTableProps<T> {
  columns: Column<T>[]
  data: T[]
  keyExtractor: (item: T) => string
  onRowClick?: (item: T) => void
  emptyMessage?: string
  externalSearchQuery?: string
  enableColumnToggle?: boolean
  hiddenColumns?: Record<string, boolean>
}

export function DataTable<T>({
  columns,
  data,
  keyExtractor,
  onRowClick,
  emptyMessage = 'No data available',
  externalSearchQuery = '',
  enableColumnToggle = true,
  hiddenColumns: controlledHiddenColumns,
}: DataTableProps<T>) {
  const { t } = useTranslation()
  const [sortColumn, setSortColumn] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>(null)
  const [internalHiddenColumns, setHiddenColumns] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {}
    columns.forEach((c) => {
      if (c.hiddenByDefault && !c.alwaysVisible) initial[String(c.key)] = true
    })
    return initial
  })
  const hiddenColumns = controlledHiddenColumns ?? internalHiddenColumns
  const isControlled = controlledHiddenColumns !== undefined

  const visibleColumns = useMemo(
    () => columns.filter((c) => c.alwaysVisible || !hiddenColumns[String(c.key)]),
    [columns, hiddenColumns],
  )

  const toggleableColumns = columns.filter((c) => !c.alwaysVisible)

  const handleSort = (columnKey: string) => {
    if (sortColumn === columnKey) {
      if (sortDirection === 'asc') {
        setSortDirection('desc')
      } else if (sortDirection === 'desc') {
        setSortColumn(null)
        setSortDirection(null)
      } else {
        setSortDirection('asc')
      }
    } else {
      setSortColumn(columnKey)
      setSortDirection('asc')
    }
  }

  const filteredAndSortedData = useMemo(() => {
    let result = [...data]

    // Search filter using external query from ListFilters
    if (externalSearchQuery.trim()) {
      const query = externalSearchQuery.toLowerCase()
      result = result.filter((item) => {
        return columns.some((col) => {
          if (col.searchable === false) return false
          const value = item[col.key as keyof T]
          if (value === null || value === undefined) return false
          return String(value).toLowerCase().includes(query)
        })
      })
    }

    // Sorting
    if (sortColumn && sortDirection) {
      result.sort((a, b) => {
        const aVal = a[sortColumn as keyof T]
        const bVal = b[sortColumn as keyof T]

        if (aVal === null || aVal === undefined) return 1
        if (bVal === null || bVal === undefined) return -1

        let comparison = 0
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          comparison = aVal - bVal
        } else {
          comparison = String(aVal).localeCompare(String(bVal))
        }

        return sortDirection === 'asc' ? comparison : -comparison
      })
    }

    return result
  }, [data, columns, externalSearchQuery, sortColumn, sortDirection])

  const getSortIcon = (columnKey: string) => {
    if (sortColumn !== columnKey) {
      return <ArrowUpDown className="w-3.5 h-3.5 text-muted-foreground/50" />
    }
    if (sortDirection === 'asc') {
      return <ArrowUp className="w-3.5 h-3.5 text-primary" />
    }
    return <ArrowDown className="w-3.5 h-3.5 text-primary" />
  }

  return (
    <div className="space-y-2 animate-slide-up">
      {enableColumnToggle && !isControlled && toggleableColumns.length > 0 && (
        <div className="flex justify-end">
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Columns className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">{t('filters.columns', 'Columns')}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-popover">
                  <DropdownMenuLabel>
                    {t('filters.toggleColumns', 'Toggle columns')}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {toggleableColumns.map((col) => {
                    const key = String(col.key)
                    return (
                      <DropdownMenuCheckboxItem
                        key={key}
                        checked={!hiddenColumns[key]}
                        onCheckedChange={() =>
                          setHiddenColumns((prev) => ({ ...prev, [key]: !prev[key] }))
                        }
                        onSelect={(e) => e.preventDefault()}
                      >
                        {col.header}
                      </DropdownMenuCheckboxItem>
                    )
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </TooltipTrigger>
            <TooltipContent>{t('tooltips.filters.columns', 'Toggle columns')}</TooltipContent>
          </Tooltip>
        </div>
      )}
      <div className="surface-card overflow-hidden">
        <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="table-head-row">
                {visibleColumns.map((column) => {
                  const isSortable = column.sortable !== false
                  return (
                    <th
                      key={String(column.key)}
                      className={cn(
                        'table-head-cell',
                        isSortable && 'table-head-cell-sortable',
                        column.className,
                      )}
                      onClick={() => isSortable && handleSort(String(column.key))}
                    >
                      <div className="flex items-center gap-1.5">
                        <span>{column.header}</span>
                        {isSortable && getSortIcon(String(column.key))}
                      </div>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={visibleColumns.length}
                    className="py-12 text-center text-muted-foreground"
                  >
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                filteredAndSortedData.map((item, index) => (
                  <tr
                    key={keyExtractor(item)}
                    className={cn('table-body-row', onRowClick && 'cursor-pointer')}
                    onClick={() => onRowClick?.(item)}
                    style={{ animationDelay: `${index * 30}ms` }}
                  >
                    {visibleColumns.map((column) => (
                      <td
                        key={String(column.key)}
                        className={cn('table-body-cell', column.className)}
                      >
                        {column.render
                          ? column.render(item)
                          : String(item[column.key as keyof T] ?? '')}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
